// Wave 2 · Batch 2K (Feb-2026) — Country → /courses/* topic-cluster reverse
// cross-links. Each of the 42 country hubs in COUNTRIES_DATA gets 3 curated
// course pillars whose relevance to that country's school ecosystem is real
// (British international schools in GCC → IGCSE/A-Level; American schools in
// East Asia → IB/AP; British-diaspora + Cambridge International in SEA →
// IGCSE/IB; UK/EU university admissions → IELTS-TOEFL).
//
// Read by OnlineCountry.jsx as `COUNTRY_COURSE_MAP[data.slug]`. No changes
// to countriesData.js — keeps that file's shape stable for the sitemap /
// hreflang machinery.

export const COURSE_LABELS = {
    "cbse": "CBSE (Class 1-12)",
    "icse-isc": "ICSE & ISC",
    "state-boards": "State Boards",
    "ib": "IB (PYP · MYP · DP)",
    "igcse": "IGCSE (Cambridge)",
    "a-level": "A-Level (AS & A2)",
    "ap": "AP (Advanced Placement)",
    "jee": "JEE Main & Advanced",
    "neet": "NEET Preparation",
    "ielts-toefl": "IELTS & TOEFL",
    "spoken-english": "Spoken English",
    "coding": "Coding & Computer Courses",
};

// Each entry: [course-slug, per-country tagline anchored on a real school /
// university / migration pathway].
export const COUNTRY_COURSE_MAP = {
    // ─── North America ───────────────────────────────────────────────
    "usa": [
        ["ap", "Stuyvesant + Bronx Science + Bellaire + Lowell — AP STEM stronghold, Ivy admissions track"],
        ["ib", "UNIS + Dwight + French American + International School of Boston — IB DP HL cohort"],
        ["ielts-toefl", "Grad-school + Common App international applicants — TOEFL iBT + IELTS Academic"],
    ],
    "canada": [
        ["ap", "US-university-track Toronto + Mississauga families sitting AP alongside OSSD"],
        ["ielts-toefl", "IELTS General + CELPIP for Canada PR + UofT / McGill / Waterloo admissions"],
        ["ib", "TFS + Branksome Hall + Ashbury + West Point Grey Academy — Canadian IB DP HL"],
    ],
    "mexico": [
        ["ib", "American School Foundation Mexico + Colegio Peterson + Greengates — IB DP HL cohort"],
        ["ap", "American-curriculum school seniors sitting AP alongside SAT for US uni track"],
        ["ielts-toefl", "US / Canadian / UK university admissions — TOEFL iBT + IELTS Academic"],
    ],

    // ─── United Kingdom & Ireland ────────────────────────────────────
    "uk": [
        ["a-level", "Westminster + St Paul's + Highgate + Manchester Grammar — Oxbridge / STEP / MAT track"],
        ["igcse", "Cambridge IGCSE + Edexcel iGCSE — UK independent schools + international families"],
        ["ib", "ACS Cobham + Southbank + King's Wimbledon + Sevenoaks — UK IB DP HL cohort"],
    ],
    "ireland": [
        ["a-level", "Nord Anglia + St Andrew's + St Kilian's — Cambridge / Edexcel A-Level to UK unis"],
        ["ielts-toefl", "Stamp-4 + Critical Skills IELTS + TCD / UCD English-medium admissions"],
        ["ib", "Nord Anglia International Dublin + St Andrew's — IB DP HL predicted-grade track"],
    ],

    // ─── Australia & NZ ──────────────────────────────────────────────
    "australia": [
        ["ib", "SCEGGS + Kambala + Redlands + Wesley + MLC — IB DP HL parallel to HSC / VCE"],
        ["a-level", "British international schools — Cambridge / Edexcel A-Level to Oxbridge"],
        ["ielts-toefl", "PTE Academic + IELTS General — Aussie skilled-visa points test + uni applicants"],
    ],
    "new-zealand": [
        ["ib", "Kristin School + ACG Parnell + ACG Strathallan + Scots College Wellington — IB DP HL"],
        ["a-level", "Cambridge International AS / A-Level — NZ private-school Cambridge track"],
        ["ielts-toefl", "IELTS General + NZ SMC / student-visa test — Kiwi-Indian diaspora + uni admissions"],
    ],

    // ─── Middle East / GCC ───────────────────────────────────────────
    "uae": [
        ["igcse", "GEMS Wellington + Dubai College + JESS + Repton — Cambridge 0580 / 0625 stronghold"],
        ["a-level", "Cambridge 9709 + Edexcel IAL — Dubai College + JESS + Repton to Russell Group"],
        ["ib", "GEMS Wellington + Dubai International Academy + Dubai American — IB DP HL cohort"],
    ],
    "saudi-arabia": [
        ["igcse", "BISR + BISJ + King Fahad Academy + Kingdom Schools — Cambridge IGCSE Extended tier"],
        ["a-level", "British-curriculum Riyadh / Jeddah senior — Cambridge / Edexcel A-Level"],
        ["ib", "Multinational School Riyadh + Continental Jeddah — IB DP HL predicted-grade track"],
    ],
    "qatar": [
        ["igcse", "Doha College + Sherborne Qatar + Compass International — Cambridge IGCSE cohort"],
        ["a-level", "Doha College + Sherborne Qatar — Cambridge / Edexcel A-Level to Russell Group"],
        ["ib", "Qatar Academy + ACS Doha + American School Doha — IB DP HL cohort"],
    ],
    "kuwait": [
        ["igcse", "British School of Kuwait + English School Kuwait + Al-Bayan Bilingual — Cambridge IGCSE"],
        ["a-level", "British-curriculum senior track — Cambridge / Edexcel A-Level to UK unis"],
        ["ib", "American International Kuwait + Universal American — IB DP HL cohort"],
    ],
    "oman": [
        ["igcse", "TAISM + British School Muscat + Al Sahwa — Cambridge IGCSE Extended tier"],
        ["a-level", "British School Muscat + American British Academy — Cambridge / Edexcel A-Level"],
        ["ib", "American International Muscat + The Sultan's School — IB DP HL cohort"],
    ],
    "bahrain": [
        ["igcse", "St Christopher's + British School of Bahrain + Nadeen — Cambridge IGCSE"],
        ["a-level", "St Christopher's + BSB — Cambridge / Edexcel A-Level to UK unis"],
        ["ib", "Ibn Khuldoon National + Bahrain Bayan — IB DP HL cohort"],
    ],

    // ─── South & Southeast Asia ──────────────────────────────────────
    "singapore": [
        ["igcse", "Tanglin Trust + Dulwich + Marlborough College Singapore — Cambridge IGCSE"],
        ["a-level", "UWCSEA + Tanglin + Stamford — Cambridge A-Level cohort to UK / US unis"],
        ["ib", "UWCSEA + SJI International + Stamford + Overseas Family — IB DP HL Math AA"],
    ],
    "malaysia": [
        ["igcse", "Marlborough College Malaysia + Alice Smith + Garden International — Edexcel iGCSE"],
        ["a-level", "Marlborough + Alice Smith — Cambridge / Edexcel A-Level to UK unis"],
        ["ib", "Marlborough + IGB International + Mont'Kiara International — IB DP HL cohort"],
    ],
    "hong-kong": [
        ["igcse", "Harrow + Kellett + English Schools Foundation — Cambridge / Edexcel IGCSE"],
        ["a-level", "Harrow + Kellett Senior — A-Level to UK / HKU pathway"],
        ["ib", "CIS + Li Po Chun + Discovery Bay International + Kellett — IB DP HL Math"],
    ],
    "thailand": [
        ["ib", "NIST Bangkok + KIS + Bangkok Patana + ISB — IB DP HL cohort"],
        ["igcse", "Bangkok Patana + Shrewsbury Bangkok + Harrow — Cambridge IGCSE"],
        ["ielts-toefl", "UK / US / Australia uni admissions — IELTS Academic + TOEFL iBT"],
    ],
    "indonesia": [
        ["ib", "Jakarta Intercultural + British School Jakarta + North Jakarta — IB DP HL cohort"],
        ["igcse", "British School Jakarta + Sinarmas World Academy — Cambridge IGCSE"],
        ["ielts-toefl", "UK / Australia / Canada uni admissions — IELTS Academic + TOEFL iBT"],
    ],
    "philippines": [
        ["ib", "International School Manila + Brent International + European International — IB DP HL"],
        ["ielts-toefl", "US / Canada / UK / Australia admissions + healthcare-worker migration — IELTS + TOEFL"],
        ["igcse", "British School Manila + Southville International — Cambridge IGCSE Extended tier"],
    ],
    "japan": [
        ["ib", "ASIJ + YIS + St. Mary's International + Nagoya International — IB DP HL cohort"],
        ["ielts-toefl", "English-medium Japanese Master's (Todai, Kyoto U) + overseas admissions — IELTS + TOEFL"],
        ["ap", "American School in Japan (ASIJ) + International School of the Sacred Heart — AP STEM track"],
    ],
    "south-korea": [
        ["ib", "Seoul Foreign + Chadwick + Dwight School Seoul + KIS — IB DP HL cohort"],
        ["ielts-toefl", "English-medium Master's (SNU, KAIST, POSTECH) + overseas admissions — IELTS + TOEFL"],
        ["ap", "Seoul American High + International Christian — AP STEM + College Board readiness"],
    ],

    // ─── Europe (Western + Nordic) ───────────────────────────────────
    "germany": [
        ["ib", "Berlin International + Frankfurt International + Bavarian International — IB DP HL cohort"],
        ["igcse", "British International Berlin + St George's Cologne — Cambridge IGCSE Extended tier"],
        ["ielts-toefl", "TU Munich + LMU + Frankfurt School English-medium Master's — IELTS + TOEFL"],
    ],
    "france": [
        ["ib", "British School Paris + Marymount + International School of Paris — IB DP HL cohort"],
        ["igcse", "British School Paris + Mougins School — Cambridge IGCSE for expat families"],
        ["ielts-toefl", "HEC + Sciences Po + Sorbonne English-medium Master's — IELTS Academic + TOEFL"],
    ],
    "netherlands": [
        ["ib", "International School of Amsterdam + The Hague + American School Rotterdam — IB DP HL"],
        ["ielts-toefl", "TU Delft + UvA + Erasmus + Utrecht English-medium — IELTS + TOEFL admissions"],
        ["igcse", "British School Netherlands + Amsterdam British — Cambridge IGCSE Extended tier"],
    ],
    "switzerland": [
        ["ib", "International School of Geneva + Zurich International + Le Rosey + Aiglon — IB DP HL"],
        ["igcse", "British School Zug + Aiglon + Institut Le Rosey — Cambridge IGCSE"],
        ["ielts-toefl", "ETH Zürich + EPFL + Uni St. Gallen English-medium admissions — IELTS + TOEFL"],
    ],
    "norway": [
        ["ib", "Oslo International + Skagerak International — IB DP HL cohort"],
        ["ielts-toefl", "NTNU + Uni of Oslo + BI Norwegian English-medium Master's — IELTS + TOEFL"],
        ["igcse", "British international schools + expat Cambridge IGCSE Extended tier"],
    ],
    "sweden": [
        ["ib", "Stockholm International + International School of the Gothenburg Region — IB DP HL"],
        ["ielts-toefl", "KTH + Lund + Uppsala + Stockholm School English-medium Master's — IELTS + TOEFL"],
        ["igcse", "British international schools + expat Cambridge IGCSE Extended tier"],
    ],
    "finland": [
        ["ib", "Ressu Comprehensive + International School of Helsinki + Espoo International — IB DP HL"],
        ["ielts-toefl", "Aalto + Uni of Helsinki + Turku English-medium Master's — IELTS + TOEFL"],
        ["igcse", "British international schools + expat Cambridge IGCSE Extended tier"],
    ],
    "denmark": [
        ["ib", "Copenhagen International + Rygaards + International School Aarhus — IB DP HL"],
        ["ielts-toefl", "DTU + Copenhagen Business School + Aarhus English-medium — IELTS + TOEFL"],
        ["igcse", "British international schools + expat Cambridge IGCSE Extended tier"],
    ],
    "belgium": [
        ["ib", "Antwerp International + International School of Brussels + BSB — IB DP HL cohort"],
        ["ielts-toefl", "KU Leuven + UCL + Ghent English-medium Master's — IELTS + TOEFL"],
        ["igcse", "British School Brussels + St John's International — Cambridge IGCSE Extended tier"],
    ],
    "italy": [
        ["ib", "St Stephen's Rome + International School Milan + American Overseas — IB DP HL cohort"],
        ["ielts-toefl", "Bocconi + Politecnico + Sapienza English-medium Master's — IELTS + TOEFL"],
        ["igcse", "St George's British Rome + Sir James Henderson Milan — Cambridge IGCSE"],
    ],
    "spain": [
        ["ib", "American School Madrid + International School Barcelona + King's — IB DP HL cohort"],
        ["ielts-toefl", "IE + ESADE + IESE + Uni Autónoma English-medium Master's — IELTS + TOEFL"],
        ["igcse", "British Council Madrid + King's College Madrid — Cambridge IGCSE Extended tier"],
    ],
    "austria": [
        ["ib", "American International Vienna + Vienna International + Danube International — IB DP HL"],
        ["ielts-toefl", "TU Wien + Uni of Vienna + WU English-medium Master's — IELTS + TOEFL"],
        ["igcse", "Vienna International British stream — Cambridge IGCSE Extended tier"],
    ],
    "portugal": [
        ["ib", "St Julian's + Carlucci American International Lisbon — IB DP HL cohort"],
        ["ielts-toefl", "Universidade de Lisboa + Nova + Porto Business English-medium — IELTS + TOEFL"],
        ["igcse", "St Julian's + Oporto British + St Dominic's — Cambridge IGCSE Extended tier"],
    ],
    "poland": [
        ["ib", "British International Warsaw + American Wroclaw + Poznan International — IB DP HL"],
        ["ielts-toefl", "Warsaw + Jagiellonian + Poznan English-medium Master's — IELTS + TOEFL"],
        ["igcse", "British International Warsaw + Meridian — Cambridge IGCSE Extended tier"],
    ],
    "czech-republic": [
        ["ib", "International School of Prague + Riverside + English College Prague — IB DP HL cohort"],
        ["ielts-toefl", "Charles University + Czech Technical + Masaryk English-medium — IELTS + TOEFL"],
        ["igcse", "English College Prague + British International — Cambridge IGCSE Extended tier"],
    ],
    "hungary": [
        ["ib", "American International Budapest + British International + SEK — IB DP HL cohort"],
        ["ielts-toefl", "Central European University + Budapest University English-medium — IELTS + TOEFL"],
        ["igcse", "British International Budapest — Cambridge IGCSE Extended tier"],
    ],
    "romania": [
        ["ib", "International School of Bucharest + American International — IB DP HL cohort"],
        ["ielts-toefl", "Bucharest + Cluj-Napoca English-medium Master's — IELTS + TOEFL"],
        ["igcse", "British School of Bucharest + Cambridge School — Cambridge IGCSE Extended tier"],
    ],

    // ─── Turkey & Israel ─────────────────────────────────────────────
    "turkey": [
        ["ib", "British International Istanbul + MEF International + Enka + Robert College — IB DP HL"],
        ["igcse", "British International Istanbul + Uskudar American Academy — Cambridge IGCSE"],
        ["ielts-toefl", "Bogazici + METU + Sabanci English-medium Master's — IELTS + TOEFL"],
    ],
    "israel": [
        ["ib", "American International Israel + Anglican International Jerusalem — IB DP HL cohort"],
        ["ap", "Walworth Barbour American International + Eastern Mediterranean — AP STEM + Ivy track"],
        ["ielts-toefl", "Technion + Hebrew University + Tel Aviv English-medium Master's — IELTS + TOEFL"],
    ],

    // ─── South Africa & LatAm ────────────────────────────────────────
    "south-africa": [
        ["igcse", "St Cyprian's + Roedean + St Andrew's — Cambridge International 0580 / 0625 stronghold"],
        ["a-level", "British-curriculum Cape Town / Johannesburg — Cambridge / Edexcel A-Level"],
        ["ib", "American International Johannesburg + Sacred Heart College — IB DP HL cohort"],
    ],
    "brazil": [
        ["ib", "Graded American São Paulo + St Paul's + British College Brazil — IB DP HL cohort"],
        ["ap", "Graded American + American School Rio de Janeiro — AP STEM + Ivy admissions track"],
        ["ielts-toefl", "USP + Unicamp + FGV English-medium Master's + US uni admissions — IELTS + TOEFL"],
    ],
};
